import { Switch, Route, useLocation } from 'wouter';
import { queryClient } from './lib/queryClient';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from '@/components/ui/toaster';
import { TooltipProvider } from '@/components/ui/tooltip';
import { useAuth } from '@/hooks/useAuth';
import SwygWaveMark from '@/components/SwygWaveMark';
import { useProfileCompletion } from '@/hooks/useProfileCompletion';
import { useEffect } from 'react';
import { initializeOneSignal, subscribeToNotifications } from '@/services/oneSignalService';
import { AddToHomeScreenPrompt } from '@/components/AddToHomeScreenPrompt';
import ErrorBoundary from '@/components/error-boundary';
import NotFound from '@/pages/not-found';
import Landing from '@/pages/landing';
import Login from '@/pages/login';
import Registration from '@/pages/registration';
import Dashboard from '@/pages/dashboard';
import Profile from '@/pages/profile';
import Leaderboard from '@/pages/leaderboard';
import Reports from '@/pages/reports';
import Rewards from '@/pages/rewards';
// NotificationsPage removed - replaced with OneSignal
import EmailVerificationPage from '@/pages/email-verification';
import ForgotPassword from '@/pages/forgot-password';
import ResetPassword from '@/pages/reset-password';
import LogoutPage from '@/pages/logout';
// New pages from UI inspiration
import HomeNew from '@/pages/home-new';
import Analytics from '@/pages/analytics';
import Content from '@/pages/content';
import Learning from '@/pages/learning';
import Onboarding from '@/pages/onboarding';
import EditProfile from '@/pages/edit-profile';
import HydrationSettings from '@/pages/hydration-settings';
import ProfileNew from '@/pages/profile-new';
// Admin panels
import HRPanel from '@/pages/hr-panel';
import ContentAdminPanel from '@/pages/content-admin';
import SuperAdminPanel from '@/pages/super-admin-panel';
import UnlockReport from '@/pages/unlock-report';

const ADMIN_ROLES = ['super_admin', 'hr_admin', 'content_admin'] as const;

function adminPanelPath(role: string | undefined | null): string | null {
  if (role === 'super_admin') return '/super-admin';
  if (role === 'hr_admin') return '/hr-panel';
  if (role === 'content_admin') return '/content-admin';
  return null;
}

function isAdminRole(role: string | undefined | null): boolean {
  return !!role && (ADMIN_ROLES as readonly string[]).includes(role);
}

/**
 * Onboarding Route - accessible without auth for new users.
 * Admins never enter onboarding — they go straight to their panel.
 * Players redirect to dashboard if profile is already complete.
 */
function OnboardingRoute() {
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const { profileStatus } = useProfileCompletion();
  const [, setLocation] = useLocation();

  useEffect(() => {
    if (authLoading) return;

    if (isAuthenticated && user) {
      const panel = adminPanelPath((user as any).role);
      if (panel) {
        setLocation(panel);
        return;
      }
    }

    if (isAuthenticated && profileStatus?.isComplete) {
      setLocation('/dashboard');
    }
  }, [isAuthenticated, authLoading, profileStatus, user, setLocation]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-hydro-100">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-200 border-t-purple-600" />
      </div>
    );
  }

  if (isAuthenticated && isAdminRole((user as any)?.role)) {
    return null;
  }

  if (isAuthenticated && profileStatus?.isComplete) {
    return null;
  }

  return <Onboarding />;
}

function ProtectedRoute({
  component: Component,
  requiresCompleteProfile = false,
}: {
  component: React.ComponentType;
  requiresCompleteProfile?: boolean;
}) {
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const { profileStatus, isLoading: profileLoading } = useProfileCompletion();
  const [, setLocation] = useLocation();
  const role = (user as any)?.role as string | undefined;
  const adminPanel = adminPanelPath(role);

  useEffect(() => {
    // Wait for both auth and profile to finish loading
    if (authLoading || (isAuthenticated && profileLoading)) {
      return;
    }

    // Not authenticated - redirect to login
    if (!isAuthenticated) {
      console.log('🔒 ProtectedRoute: Not authenticated, redirecting to /login');
      const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
      setLocation(`/login?returnUrl=${returnUrl}`);
      return;
    }

    // Admins never go through player onboarding / profile-completion gates
    if (adminPanel) {
      const path = window.location.pathname;
      const onAdminPanel =
        path.startsWith('/hr-panel') ||
        path.startsWith('/super-admin') ||
        path.startsWith('/content-admin');
      if (!onAdminPanel && requiresCompleteProfile) {
        console.log('🛡️ ProtectedRoute: Admin on player route, redirecting to panel', adminPanel);
        setLocation(adminPanel);
      }
      return;
    }

    // Authenticated but profile incomplete and route requires complete profile
    if (requiresCompleteProfile && profileStatus && !profileStatus.isComplete) {
      console.log('⚠️ ProtectedRoute: Profile incomplete, redirecting to /onboarding');
      console.log('📊 Profile status:', profileStatus);
      setLocation('/onboarding');
      return;
    }

    if (requiresCompleteProfile) {
      console.log('✅ ProtectedRoute: Profile complete, rendering component');
      console.log('📊 Profile status:', profileStatus);
    }
  }, [
    isAuthenticated,
    authLoading,
    profileLoading,
    profileStatus,
    requiresCompleteProfile,
    adminPanel,
    setLocation,
  ]);

  // Show loading state while checking auth/profile
  if (authLoading || (isAuthenticated && profileLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  // Not authenticated
  if (!isAuthenticated) {
    return null; // Will redirect in useEffect
  }

  // Admins on player routes that require a complete profile → redirect to panel
  if (adminPanel && requiresCompleteProfile) {
    const path = window.location.pathname;
    const onAdminPanel =
      path.startsWith('/hr-panel') ||
      path.startsWith('/super-admin') ||
      path.startsWith('/content-admin');
    if (!onAdminPanel) {
      return null;
    }
  }

  // Profile incomplete but route requires complete profile (players only)
  if (!adminPanel && requiresCompleteProfile && profileStatus && !profileStatus.isComplete) {
    return null; // Will redirect in useEffect
  }

  // All checks passed - render component
  return <Component />;
}

/**
 * Home Route Component
 * Redirects authenticated users to dashboard, others to landing
 * Admin users bypass profile-setup and go directly to their panel
 */
function HomeRoute() {
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const { profileStatus, isLoading: profileLoading } = useProfileCompletion();
  const [, setLocation] = useLocation();

  useEffect(() => {
    // Wait for loading to complete
    if (authLoading || (isAuthenticated && profileLoading)) {
      return;
    }

    // Redirect authenticated users
    if (isAuthenticated && user) {
      const role = (user as any).role;

      // Admin users bypass profile setup and go to their panel
      if (role === 'super_admin') {
        setLocation('/super-admin');
        return;
      }
      if (role === 'hr_admin') {
        setLocation('/hr-panel');
        return;
      }
      if (role === 'content_admin') {
        setLocation('/content-admin');
        return;
      }

      // Regular users: check profile completion
      if (profileStatus && !profileStatus.isComplete) {
        setLocation('/onboarding');
      } else {
        setLocation('/dashboard');
      }
    }
  }, [isAuthenticated, authLoading, profileLoading, profileStatus, user, setLocation]);

  // Show loading while checking
  if (authLoading || (isAuthenticated && profileLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  // Show landing page for unauthenticated users
  if (!isAuthenticated) {
    return <Landing />;
  }

  // Will redirect in useEffect
  return null;
}

/**
 * Unified AppContent with all routes registered
 * No conditional route rendering to prevent flickering
 */
function AppContent() {
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();

  // Initialize OneSignal when user is authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      // CRITICAL: Add small delay to ensure session cookies are fully set
      // This prevents 401 errors when subscribing to OneSignal
      const timer = setTimeout(() => {
        initializeOneSignal()
          .then(() => {
            console.log('✅ OneSignal initialized');
            // Auto-subscribe authenticated users
            return subscribeToNotifications(user.id);
          })
          .then(() => {
            console.log('✅ Subscribed to OneSignal notifications');
          })
          .catch((error) => {
            console.error('❌ OneSignal initialization/subscription failed:', error);
          });
      }, 200); // 200ms delay to ensure cookies are set

      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, user]);

  // Show loading state only on initial load
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-blue-600 mx-auto mb-4"></div>
          <SwygWaveMark size="lg" className="mx-auto mb-4" />
        </div>
      </div>
    );
  }

  // Render ALL routes - protection is handled by ProtectedRoute component
  return (
    <TooltipProvider>
      <Toaster />
      {/* iOS Add to Home Screen prompt for push notifications */}
      <AddToHomeScreenPrompt />
      <Switch>
        {/* Home Route - Smart redirect based on auth status */}
        <Route path="/" component={HomeRoute} />

        {/* Public Routes */}
        <Route path="/login" component={Login} />
        <Route path="/registration" component={Registration} />
        <Route path="/forgot-password" component={ForgotPassword} />
        <Route path="/reset-password" component={ResetPassword} />
        <Route path="/email-verification" component={EmailVerificationPage} />
        <Route path="/logout" component={LogoutPage} />

        {/* Onboarding Routes (authenticated, profile incomplete) */}
        <Route path="/profile-setup">{() => <OnboardingRoute />}</Route>
        <Route path="/preferences-setup">{() => <OnboardingRoute />}</Route>

        {/* Protected Routes (authenticated, profile complete) */}
        <Route path="/dashboard">
          {() => <ProtectedRoute component={Dashboard} requiresCompleteProfile={true} />}
        </Route>
        <Route path="/profile">
          {() => <ProtectedRoute component={Profile} requiresCompleteProfile={true} />}
        </Route>
        <Route path="/edit-profile">
          {() => <ProtectedRoute component={EditProfile} requiresCompleteProfile={true} />}
        </Route>
        <Route path="/hydration-settings">
          {() => <ProtectedRoute component={HydrationSettings} requiresCompleteProfile={true} />}
        </Route>
        <Route path="/leaderboard">
          {() => <ProtectedRoute component={Leaderboard} requiresCompleteProfile={true} />}
        </Route>
        <Route path="/reports">
          {() => <ProtectedRoute component={Reports} requiresCompleteProfile={true} />}
        </Route>
        <Route path="/rewards">
          {() => <ProtectedRoute component={Rewards} requiresCompleteProfile={true} />}
        </Route>
        {/* Notifications route removed - replaced with OneSignal */}

        {/* New UI Inspiration Routes */}
        <Route path="/home-new">
          {() => <ProtectedRoute component={HomeNew} requiresCompleteProfile={true} />}
        </Route>
        <Route path="/analytics">
          {() => <ProtectedRoute component={Analytics} requiresCompleteProfile={true} />}
        </Route>
        <Route path="/unlock-report">
          {() => <ProtectedRoute component={UnlockReport} requiresCompleteProfile={true} />}
        </Route>
        <Route path="/content">
          {() => <ProtectedRoute component={Content} requiresCompleteProfile={true} />}
        </Route>
        <Route path="/learning">
          {() => <ProtectedRoute component={Learning} requiresCompleteProfile={true} />}
        </Route>
        <Route path="/onboarding-new">{() => <OnboardingRoute />}</Route>
        <Route path="/onboarding">{() => <OnboardingRoute />}</Route>
        <Route path="/profile-new">
          {() => <ProtectedRoute component={ProfileNew} requiresCompleteProfile={true} />}
        </Route>

        {/* Admin Routes - No profile required for admin access */}
        <Route path="/hr-panel">{() => <ProtectedRoute component={HRPanel} />}</Route>
        <Route path="/content-admin">
          {() => <ProtectedRoute component={ContentAdminPanel} />}
        </Route>
        <Route path="/super-admin">{() => <ProtectedRoute component={SuperAdminPanel} />}</Route>

        {/* 404 */}
        <Route component={NotFound} />
      </Switch>
    </TooltipProvider>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AppContent />
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
